import React, { useContext, useEffect, useState } from 'react';
import { Card } from '@bb/elements-web';
import styled from 'styled-components';
import { AuthContext } from 'contexts';
import { useAnsweringServiceDetails } from 'hooks';
import CurrentServicesSection from './components/CurrentServicesSection';
import AvailableServicesSection from './components/AvailableServicesSection';
import { PlanTier, OrgPlanPayer } from './components/OrgPlanCard';
import { ViewingLevel } from '../..';

export interface AllSubscriptionsCardProps {
  /** ID of the user viewing this page */
  userId: string;
  /** Whether these are the subscriptions for an indivdual user or for a brokerage paying for all its users */
  viewingLevel: ViewingLevel;
  /** Callback function when the Org Plan (i.e. Tier) button is clicked */
  onOrgPlanClick: () => void;
  /** Callback function when the Answering Service button is clicked */
  onAnsweringServiceClick: () => void;
}

/**
 * Main subscriptions card displaying a user or brokerage's current services
 * (i.e. active subscriptions) and available services they can sign up for.
 */
const AllSubscriptionsCard: React.FC<AllSubscriptionsCardProps> = ({
  userId,
  viewingLevel,
  onOrgPlanClick,
  onAnsweringServiceClick,
}) => {
  // #region States & variables

  const { isOrgOnBrokeragePlan, isOrgPlanUpgraded } = useContext(AuthContext);
  /** The type of Org Plan this brokerage is on. Agent Plan (Tier 1) or Brokerage Plan (Tier 2). */
  const currentOrgPlan: PlanTier =
    isOrgOnBrokeragePlan || isOrgPlanUpgraded ? 'tier2' : 'tier1';
  /** How the Org Plan subscription is billed */
  const orgPlanPayer: OrgPlanPayer =
    isOrgOnBrokeragePlan && !isOrgPlanUpgraded ? 'boardOrMls' : 'brokerage';

  const {
    answeringServiceType,
    answeringServicePayer,
    hasActiveAnsweringService,
  } = useAnsweringServiceDetails({ viewingLevel, userId });

  // Set whether we encountered the error during the data fetch so we can disable the button to
  const [fetchDataError, setFetchDataError] = useState<boolean>(false);
  useEffect(() => {
    if (!answeringServiceType || !answeringServicePayer) {
      setFetchDataError(true);
    } else {
      setFetchDataError(false);
    }
  }, [answeringServiceType, answeringServicePayer]);
  // #endregion States & variables
  // #region JSX to return

  return (
    <StyledCard dataCy="all-subscriptions-card">
      <ContentContainer>
        <CurrentServicesSection
          fetchDataError={fetchDataError}
          currentOrgPlan={currentOrgPlan}
          orgPlanPayer={orgPlanPayer}
          hasActiveAnsweringService={hasActiveAnsweringService}
          answeringServiceType={answeringServiceType}
          answeringServicePayer={answeringServicePayer}
          onOrgPlanClick={onOrgPlanClick}
          onAnsweringServiceClick={onAnsweringServiceClick}
        />
        <AvailableServicesSection
          fetchDataError={fetchDataError}
          hasActiveAnsweringService={hasActiveAnsweringService}
          answeringServiceType={answeringServiceType}
          answeringServicePayer={answeringServicePayer}
          onAnsweringServiceClick={onAnsweringServiceClick}
        />
      </ContentContainer>
    </StyledCard>
  );
};

// #endregion JSX to return

export default AllSubscriptionsCard;

const StyledCard = styled(Card)`
  .ant-card-body {
    padding: 24px;
  }
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;
